/**
 * ENG-10760: a host with no agents assigned polls exactly as hard as a busy one.
 *
 * WHAT THIS IS FOR, MEASURED. Host `d0a78e4f…` (Zanda BYO, account
 * `585819234046`) has had zero agents since 2026-09-16 and still issued
 * **25,359 requests/day — 1.06% of ALL API traffic**. The breakdown over the 24h
 * to 2026-09-20 decomposes onto the loop structure to within 3 requests, which
 * is what makes the fix locatable rather than a guess:
 *
 *   poll pass (`/host/agents` + `/host/kanban/recurring/spawn` +
 *     `/host/heartbeat`), ~11.6s apart      7,429 x 3 = 22,287
 *   `livenessTimer` -> `/host/heartbeat`, 30s           2,880
 *   `/host/exchange` + `/host/usage-readings`, ~15 min    192
 *                                                     -------
 *                                                      25,359
 *
 * The poll pass is 88% of it, and none of that pass is per-agent: `pollCycle`
 * does its host-level work and then walks an empty agent list. So the whole cost
 * is being paid to re-learn a fact that has not changed in four days.
 *
 * WHY THE DELAY AND NOT A SKIP. `manager-worker.ts` re-arms the poll timer on
 * every path on purpose (ENG-9068): a caller that declines to re-arm can end the
 * last poll chain, leaving the manager alive and heartbeating and never polling
 * again — the failure ENG-9637's throttle docstring goes out of its way not to
 * reintroduce. This lengthens the gap and always re-arms; it can never stop the
 * loop.
 *
 * AND WHY IT COMPOSES WITH ENG-5041 RATHER THAN COMPETING. `scheduleNext` takes
 * `Math.max(intervalMs, failureBackoffMs)`; this adds one more term to that max.
 * Two independent backoffs with two different reset conditions racing over one
 * timer is the shape that produces a host which never wakes up, so there is
 * deliberately no second timer and no second scheduler here — only a longer
 * delay that the existing one already knows how to apply.
 *
 * WHAT AN EMPTY RESPONSE MEANS, AND WHAT A FAILED ONE DOES NOT. Only a
 * SUCCESSFUL `/host/agents` that returned zero agents counts. A transport error
 * tells you nothing about the roster, and treating it as empty would back the
 * host off exactly when it has lost contact — the inverse of what it needs. This
 * mirrors `fetch-cadence.ts`, where `markFetched` is called only on success for
 * the same reason.
 */

/**
 * The ceiling, and it is not a free choice — four independent limits bear on it
 * and this is the lowest:
 *
 *  1. `UPDATE_CHECK_INTERVAL_MS` (5 min, `manager-worker.ts`). Manager
 *     self-update is checked on the poll pass. An agent-less host makes ZERO
 *     `/host/refresh` calls, so the per-agent delivery path never reaches it and
 *     the poll pass is the only channel that can ever ship it a fix — including
 *     a fix to this file. A cap above the update interval would make the manager
 *     slower to collect its own repairs than its own check interval.
 *  2. The exchange JWT's 1h TTL. `/host/exchange` is host-grained and
 *     cache-bounded (ENG-10093); 5 min leaves ~12x margin, so the credential
 *     cannot expire while the host is backed off. This is the whole reason the
 *     cap is stated in minutes and not hours.
 *  3. First-agent provisioning latency. The assignment doorbell exists
 *     (`realtime-chat.ts`, `host_agents` INSERT), but per ENG-9309 a
 *     `postgres_changes` denial SUCCEEDS the subscribe, logs `channel
 *     connected`, and then delivers nothing for ever — and the RLS helper
 *     resolves through `host_agents`, which is the table an agent-less host has
 *     no row in. The push is therefore treated as UNPROVEN for this population,
 *     and the cap is chosen so that it alone is an acceptable wait.
 *  4. Host-level controls that ride `/host/agents`, notably
 *     `restart_requested_at`, are delayed by up to the cap.
 */
export const IDLE_POLL_BACKOFF_CAP_MS = 5 * 60_000;

/**
 * Operator override for the cap. A tunable, deliberately not a gate.
 *
 * It is also, deliberately, the off switch. `scheduleNext` takes the LARGER of
 * the poll interval and this backoff, so a cap at or below the interval leaves
 * the cadence untouched — which is the escape hatch an operator needs when a
 * fleet-wide cadence change goes wrong, without a new `_ENABLED` variable that
 * `check-new-env-gates.sh` would reject (and rightly: per ADR-0022 an
 * org/team/host-scoped gate belongs in `FLAG_REGISTRY`, not a raw env read).
 */
export const IDLE_POLL_BACKOFF_CAP_ENV = 'AGT_IDLE_POLL_BACKOFF_CAP_MS';

/**
 * Hard ceiling on the override, because limit 2 above is otherwise only a
 * property of the DEFAULT.
 *
 * `/host/exchange` is driven from the poll pass, so for an idle host the cap IS
 * the re-mint cadence. An operator who set the tunable to two hours would let the
 * 1-hour exchange JWT lapse while the host slept — after which it cannot call
 * home to be told it has an agent, which is the single thing an agent-less host
 * exists to find out. AC3 says the exchange cadence "cannot" let the JWT expire;
 * enforcing that only for the value we happen to ship makes it a convention
 * rather than a guarantee, so the override is clamped here instead.
 *
 * Clamped rather than rejected, following `IMPERSONATION_JWT_TTL_SECONDS`: an
 * out-of-range value expresses a real intent ("back off harder"), and honouring
 * as much of it as is safe beats silently reverting to a default the operator was
 * trying to move away from.
 */
export const IDLE_POLL_BACKOFF_MAX_CAP_MS = 10 * 60_000;

/**
 * Read the cap from the environment, falling back to `IDLE_POLL_BACKOFF_CAP_MS`.
 *
 * A value at or below zero is rejected rather than honoured: zero would read as
 * "no cap" to a `Math.min` and is far more likely to be an unset variable that
 * expanded to empty than a considered choice to remove the ceiling.
 *
 * The check is applied AFTER flooring, not before, and that ordering is the whole
 * correctness of it. Checking `parsed <= 0` first admits any positive fraction —
 * `0.5` floors to `0`, `currentIdlePollBackoffMs` then reads `capMs <= 0` and
 * returns 0, and the backoff is silently OFF. That is the exact outcome this
 * function's contract says it prevents, reachable by setting the variable to a
 * value that looks like a deliberate number. Found by CodeRabbit on #6276.
 */
export function resolveIdlePollBackoffCapMs(
  env: Record<string, string | undefined> = process.env,
): number {
  const raw = (env[IDLE_POLL_BACKOFF_CAP_ENV] ?? '').trim();
  if (raw === '') return IDLE_POLL_BACKOFF_CAP_MS;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return IDLE_POLL_BACKOFF_CAP_MS;
  const floored = Math.floor(parsed);
  if (floored <= 0) return IDLE_POLL_BACKOFF_CAP_MS;
  return Math.min(floored, IDLE_POLL_BACKOFF_MAX_CAP_MS);
}

/**
 * The inter-poll delay to apply after `consecutiveEmptyAgentPolls` successful
 * `/host/agents` responses that each carried zero agents.
 *
 * Returns **0** for a host that has any agent, so a busy host's timing is
 * byte-identical to before — the caller already does
 * `Math.max(intervalMs, …)`, and 0 falls straight through it. That is the same
 * contract `currentPollBackoffMs` uses for a healthy host, so the two terms read
 * the same way at the call site.
 *
 * Doubling starts immediately (`intervalMs * 2^n`) rather than at `2^(n-1)`,
 * because the first empty pass is already evidence: the roster was empty a
 * moment ago and nothing pushes a change we are willing to rely on. At the
 * default 10s interval the sequence is 20s, 40s, 80s, 160s, then the 5-minute
 * cap — about 5 minutes of wall clock to reach it, so a freshly created host
 * that is about to receive its first agent is still polling briskly through the
 * window where that is most likely.
 */
export function currentIdlePollBackoffMs(
  consecutiveEmptyAgentPolls: number,
  intervalMs: number,
  capMs: number = IDLE_POLL_BACKOFF_CAP_MS,
): number {
  if (!Number.isFinite(consecutiveEmptyAgentPolls) || consecutiveEmptyAgentPolls <= 0) return 0;
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) return 0;
  if (!Number.isFinite(capMs) || capMs <= 0) return 0;
  // Bound the exponent before it reaches Math.pow. A long-lived idle host
  // accumulates thousands of empty passes and 2^1000 is Infinity.
  //
  // This is for LEGIBILITY, not correctness, and the distinction is worth
  // stating because it is testable and the clamp is not: `Math.min(capMs,
  // Infinity)` is `capMs`, so removing this line changes no return value, and no
  // test of this function can catch its removal. It is here so the arithmetic
  // stays finite for a reader and for any future caller that wants the
  // pre-clamped value — not because the cap below needs help.
  const exponent = Math.min(consecutiveEmptyAgentPolls, 32);
  const expDelay = intervalMs * Math.pow(2, exponent);
  return Math.min(capMs, expDelay);
}
